import { EllipsisVerticalIcon } from '@heroicons/react/24/outline';
import { Menu, MenuHandler, Button, MenuList, MenuItem } from '@material-tailwind/react';
import { Fragment, FunctionComponent, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { InputDate } from '../../components/Inputs/InputDate';
import { Config } from '../../config/config';
import { useApi } from '../../providers/ApiProvider';
import { GoodApiResponse, ErrorApiResponse } from '../../types/api';
import { Convocation } from '../../types/SalariesRessources';
import { handleError } from '../../utils/ErrorHandler';
import { FormationSessionDisplay } from '../../components/FormationSessionDisplay';
import { DownloadLink } from '../../components/DownloadLink';

/**
 * Ce composant affiche la liste des convocations côté salarié.
 */
export const MesConvocationScreen: FunctionComponent = () => {
  /**
   * On stocke si on est en train d'utiliser l'API ou non.
   */
  const [apiIsLoading, setApiIsLoading] = useState(false);

  /**
   * Date du jour.
   * @constant
   */
  const currentDate = new Date();

  /**
   * Date d'il y a 7 jour.
   * @constant
   */
  const sevenDayAgo = new Date(new Date().setDate(currentDate.getDate() - 7));

  /**
   * État local qui gère le champ de la date de départ de la recherche de convocation.
   * @defaultValue La value stocké dans l'état local ou la date du jour au format MySQL
   */
  const [from, setFrom] = useState<string>(
    localStorage.getItem(`MesConvocationsScreen-From`) ||
      `${sevenDayAgo.getFullYear()}-${sevenDayAgo.getMonth() < 9 ? `0${sevenDayAgo.getMonth() + 1}` : sevenDayAgo.getMonth() + 1}-${
        sevenDayAgo.getDate() < 10 ? `0${sevenDayAgo.getDate()}` : sevenDayAgo.getDate()
      }`,
  );

  /**
   * État local qui gère le champ de la date de départ de la recherche de convocation.
   * @defaultValue La value stocké dans l'état local ou la date du jour au format MySQL
   */
  const [to, setTo] = useState<string>(
    localStorage.getItem(`MesConvocationsScreen-To`) ||
      `${currentDate.getFullYear()}-${currentDate.getMonth() < 9 ? `0${currentDate.getMonth() + 1}` : currentDate.getMonth() + 1}-${
        currentDate.getDate() < 10 ? `0${currentDate.getDate()}` : currentDate.getDate()
      }`,
  );

  /**
   * Permet de stocker la liste des convocations.
   */
  const [convocations, setConvocations] = useState<Convocation[] | null>(null);

  /**
   * On à besoin d'initialiser le client d'API à l'aide d'un hook personalisé.
   */
  const client = useApi();

  /**
   * Permet au composant de changer de page.
   */
  const navigate = useNavigate();

  /**
   * Permet de recharger les données au chargement de page
   */
  useEffect(() => {
    if (from && to) {
      handleIndex();
    }
  }, [client, from, to]);

  /**
   * Retrouve par le réseau les convocations et les met dans un état local.
   */
  const handleIndex = () => {
    if (apiIsLoading === true) {
      return;
    }

    setApiIsLoading(true);
    client
      .get<GoodApiResponse<Convocation[]>>(`/salarie/convocations`, { params: { from, to } })
      .then((response) => {
        setConvocations(response.data.data);
      })
      .catch((error: ErrorApiResponse<Convocation[]>) => {
        handleError(error);
      })
      .finally(() => {
        setApiIsLoading(false);
      });
  };

  return (
    <Fragment>
      <Helmet>
        <title>Mes convocations - {Config.app_label}</title>
      </Helmet>
      <div className='px-4 py-4 md:py-3 bg-gray-100 rounded-tl-lg rounded-tr-lg flex flex-col sm:flex-row gap-7 items-center'>
        <div className='sm:flex items-center justify-between'>
          <h1 className='text-base sm:text-lg md:text-xl poppins-bold leading-normal text-secondary-1'>Mes convocations</h1>
        </div>
        <div className='flex flex-col md:flex-row gap-4 items-center'>
          <div className='flex flex-row items-center gap-3'>
            <p className='text-xs text-secondary-1 mb-1'>Du&nbsp;:</p>
            <InputDate
              value={from}
              onChange={(e) => {
                setFrom(e.target.value);
                localStorage.setItem(`MesConvocationsScreen-From`, e.target.value);
              }}
            />
          </div>
          <div className='flex flex-row items-center gap-3'>
            <p className='text-xs text-secondary-1 mb-1'>Au&nbsp;:</p>
            <InputDate
              value={to}
              onChange={(e) => {
                setTo(e.target.value);
                localStorage.setItem(`MesConvocationsScreen-To`, e.target.value);
              }}
            />
          </div>
        </div>
      </div>
      <div>
        <div className='bg-white rounded-b-lg w-full overflow-x-auto'>
          {convocations && convocations.length > 0 ? (
            <table className='table-fixed min-w-full bg-white rounded-b-lg'>
              <thead className='text-sm text-gray-500 text-left border-b'>
                <tr>
                  <th className='py-4 text-secondary-1 px-4 text-left text-sm tracking-normal leading-4'>Formation et session</th>
                  <th className='py-4 text-secondary-1 px-4 text-left text-sm tracking-normal leading-4'>Fichier</th>
                  <th className='py-4 text-secondary-1 px-4 text-left text-sm tracking-normal leading-4'>Date de convocation</th>
                  <th className='py-4 text-secondary-1 px-4 text-left text-sm tracking-normal leading-4'>Reçue le</th>
                  <th className='py-4 text-secondary-1 px-4 text-left text-sm tracking-normal leading-4 w-[70px]'></th>
                </tr>
              </thead>
              <tbody>
                {convocations.map((convocation) => (
                  <tr
                    key={convocation.token as string}
                    className=' border-gray-100 border-b hover:bg-gray-100'
                    onDoubleClick={() => navigate(`/mes-convocations/${convocation.token}`)}>
                    <td className='px-4 py-3'>
                      <FormationSessionDisplay formation={convocation.formation} groupe={convocation.groupe} />
                    </td>
                    <td className='px-4 py-3 text-gray-800 text-sm'>
                      {convocation.fichier && <DownloadLink fichier={convocation.fichier} titre={convocation.titre} />}
                    </td>
                    <td className='px-4 py-3 text-gray-800 text-sm'>
                      {convocation.date_convocation
                        ? new Intl.DateTimeFormat('default', {
                            year: 'numeric',
                            month: 'numeric',
                            day: 'numeric',
                            hour12: false,
                          }).format(new Date(convocation.date_convocation as string))
                        : ''}
                    </td>
                    <td className='px-4 py-3 text-gray-800 text-sm'>
                      {convocation.created_at
                        ? new Intl.DateTimeFormat('default', {
                            year: 'numeric',
                            month: 'numeric',
                            day: 'numeric',
                            hour12: false,
                          }).format(new Date(convocation.created_at as string))
                        : ''}
                    </td>
                    <td className='px-4 py-3 text-secondary-1 text-sm'>
                      <Menu placement='right-start'>
                        <MenuHandler>
                          <Button
                            variant='text'
                            className='w-9 h-9 p-1.5 rounded-full text-gray-500 block mx-auto text-center mr-0 hover:bg-white/100 active:bg-transparent'>
                            <EllipsisVerticalIcon className='h-6 w-6' />
                          </Button>
                        </MenuHandler>
                        <MenuList>
                          <MenuItem onClick={() => navigate(`/mes-convocations/${convocation.token}`)}>Voir</MenuItem>
                        </MenuList>
                      </Menu>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className='text-center px-4 py-7'>Vous n'avez pas de convocation dans la période choisie.</div>
          )}
        </div>
      </div>
    </Fragment>
  );
};
